import React, { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Row, Col, Form, InputGroup, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faSearch } from '@fortawesome/free-solid-svg-icons';
import FilterButtonGroup from 'components/common/FilterButtonGroup';
import { FilterMenu } from 'components/common/FilterButtonGroup';

interface TransactionFiltersProps {
  // Search state
  searchValue: string;
  onSearchChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onSearchKeyPress: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onClearSearch: () => void;
  
  // Date filters
  fromDate: string;
  toDate: string;
  onFromDateChange: (value: string) => void;
  onToDateChange: (value: string) => void;
  onClearFromDate: () => void;
  onClearToDate: () => void;
  
  // Type filter
  selectedType: string;
  onTypeChange: (value: string) => void;
  onClearType: () => void;
  
  // Optional components to display inline with filters
  customizeButton?: React.ReactNode;
  actionButtons?: React.ReactNode;
}

const TransactionFilters = forwardRef<HTMLInputElement, TransactionFiltersProps>(
  (props, ref) => {
    const { t } = useTranslation();
    const {
      searchValue,
      onSearchChange,
      onSearchKeyPress,
      onClearSearch,
      
      fromDate,
      toDate,
      onFromDateChange,
      onToDateChange,
      onClearFromDate,
      onClearToDate,
      
      selectedType,
      onTypeChange,
      onClearType,
      
      customizeButton,
      actionButtons
    } = props;
    
    return (
      <>
        {/* Search and Filter Controls */}
        <Row className="g-3 mb-3">
          <Col xs={12} md={6} xl={3}>
            <Form.Group>
              <Form.Label>{t("transactions.filters.searchLabel", "Search Transactions")}</Form.Label>
              <InputGroup>
                <InputGroup.Text className="bg-transparent">
                  <FontAwesomeIcon icon={faSearch} className="text-body-tertiary" />
                </InputGroup.Text>
                <Form.Control
                  ref={ref}
                  placeholder={t("transactions.filters.searchPlaceholder", "Search by transaction, bank ID, or order number")}
                  value={searchValue}
                  onChange={onSearchChange}
                  onKeyPress={onSearchKeyPress}
                />
                {searchValue && (
                  <Button 
                    variant="light" 
                    className="border"
                    onClick={onClearSearch}
                  >
                    <FontAwesomeIcon icon={faTimes} />
                  </Button>
                )}
              </InputGroup>
              <Form.Text className="text-muted">
                {t("transactions.filters.searchHelp", "Search across transaction IDs, bank transaction IDs, and order numbers")}
              </Form.Text>
            </Form.Group>
          </Col>
          <Col xs={12} md={6} xl={3}>
            <Form.Group>
              <Form.Label>{t("transactions.filters.fromDate", "From Date")}</Form.Label>
              <InputGroup>
                <Form.Control
                  type="date"
                  value={fromDate}
                  onChange={(e) => onFromDateChange(e.target.value)}
                  placeholder="YYYY-MM-DD"
                />
                {fromDate && (
                  <Button 
                    variant="light" 
                    className="border"
                    onClick={onClearFromDate}
                  >
                    <FontAwesomeIcon icon={faTimes} />
                  </Button>
                )}
              </InputGroup>
            </Form.Group>
          </Col>
          <Col xs={12} md={6} xl={3}>
            <Form.Group>
              <Form.Label>{t("transactions.filters.toDate", "To Date")}</Form.Label>
              <InputGroup>
                <Form.Control
                  type="date"
                  value={toDate}
                  onChange={(e) => onToDateChange(e.target.value)}
                  placeholder="YYYY-MM-DD"
                  min={fromDate}
                />
                {toDate && (
                  <Button 
                    variant="light" 
                    className="border"
                    onClick={onClearToDate}
                  >
                    <FontAwesomeIcon icon={faTimes} />
                  </Button>
                )}
              </InputGroup>
            </Form.Group>
          </Col>
          <Col xs={12} md={6} xl={2}>
            <Form.Group>
              <Form.Label>{t("transactions.filters.type", "Type")}</Form.Label>
              <InputGroup>
                <Form.Select
                  value={selectedType}
                  onChange={(e) => onTypeChange(e.target.value)}
                >
                  <option value="">{t("transactions.filters.allTypes", "All Types")}</option>
                  <option value="Manual">{t("transactions.types.Manual", "Manual Payment")}</option>
                  <option value="Refund Transaction">{t("transactions.types.Refund Transaction", "Refund")}</option>
                  <option value="stock_fee">{t("transactions.types.stock_fee", "Stock Fee")}</option>
                  <option value="stock_withdraw">{t("transactions.types.stock_withdraw", "Stock Withdrawal")}</option>
                  <option value="delay_fee">{t("transactions.types.delay_fee", "Delay Fee")}</option>
                  <option value="return_fee">{t("transactions.types.return_fee", "Return Fee")}</option>
                  {/* <option value="rev_stock_fee">{t("transactions.types.rev_stock_fee", "Rev. Stock Fee")}</option>
                  <option value="rev_stock_withdraw">{t("transactions.types.rev_stock_withdraw", "Rev. Stock Withdrawal")}</option> */}
                  <option value="rev_delay_fee">{t("transactions.types.rev_delay_fee", "Rev. Delay Fee")}</option>
                  <option value="rev_return_fee">{t("transactions.types.rev_return_fee", "Rev. Return Fee")}</option>
                </Form.Select>
                {selectedType && (
                  <Button 
                    variant="light" 
                    className="border"
                    onClick={onClearType}
                  >
                    <FontAwesomeIcon icon={faTimes} />
                  </Button>
                )}
              </InputGroup>
            </Form.Group>
          </Col>
        </Row>
        
        {/* Custom button and actions area */}
        <div className="d-flex flex-wrap gap-3 align-items-center justify-content-between mb-3">
          {/* Display customize button if provided */}
          {customizeButton && (
            <div className="d-flex align-items-center">
              {customizeButton}
            </div>
          )}
          
          {/* Action buttons section */}
          {actionButtons && (
            <div className="ms-auto d-flex align-items-center">
              {actionButtons}
            </div>
          )}
        </div>
      </>
    );
  }
);

// Add display name for better debugging in React DevTools
TransactionFilters.displayName = 'TransactionFilters';

export default TransactionFilters;
